// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// gen.ts — the board, from a seed. Deterministic, integer-only, no clock and no Math.random.
//
// THE GENERATOR'S OWN GUARANTEES ARE VERIFIED, NOT ASSERTED. A sibling game in this fleet shipped a
// generator whose header claimed a deadlock was "unreachable by construction"; an independent
// re-derivation measured 3.2% of its floors unsolvable, because the argument was sound on the
// spanning tree and the level is not the spanning tree. So `validate()` below re-derives every
// promise from the finished board — seams diggable, wharf reachable, a starter line that actually
// routes — and generate() re-rolls until it passes, with the re-roll count asserted at zero across
// the balance suite's seeds rather than assumed to be zero.
//
// THE BOARD OPENS MID-FACTORY, AND THAT IS A DESIGN REQUIREMENT RATHER THAN A CONVENIENCE. A seed
// hands you one digger, one smelter and a lane to the wharf, already running and already shipping
// billets at a scrip apiece. The game is legible in the first five seconds — you can see items
// moving and money arriving — and the whole run is the upgrade from billets at 1 to armatures at 6,
// which is a route problem before it is an economic one. An empty board would have put the game's
// identity ninety seconds behind a construction phase.

import { makeRng } from '@ben-gy/game-engine/rng';
import { Kind, KindT, QUIRKS, Quirk, QUIRK_FAMILY } from './modes';
import { H, Layout, N, W, createState, cx, cy, idx, run, step } from './sim';

export interface Board {
  layout: Layout;
  quirks: Quirk[];
  wharf: number[];
  /** Re-rolls the generator needed before the board passed validate(). Asserted 0 in the suite. */
  rerolls: number;
}

const shuffled = <T>(rng: () => number, xs: T[]): T[] => {
  const a = xs.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    const t = a[i];
    a[i] = a[j];
    a[j] = t;
  }
  return a;
};
const pick = <T>(rng: () => number, xs: T[]): T => xs[Math.floor(rng() * xs.length)];

/** Exactly two quirks, from two different families. */
export function quirksFor(rng: () => number): Quirk[] {
  const fams = shuffled(rng, QUIRK_FAMILY.slice()).slice(0, 2);
  return fams.map((f) => pick(rng, QUIRKS.filter((q) => q.family === f)));
}

const has = (qs: Quirk[], id: string): boolean => qs.some((q) => q.id === id);

/**
 * Shortest walkable path between two cells over EMPTY ground, breaking ties by compass order so two
 * devices agree. Returns the cells strictly between `from` and `to`, or null.
 */
export function route(kind: Uint8Array, from: number, to: number, patch?: Uint8Array): number[] | null {
  const prev = new Int32Array(N).fill(-2);
  const q = [from];
  prev[from] = -1;
  for (let head = 0; head < q.length; head++) {
    const c = q[head];
    if (c === to) break;
    for (let d = 0; d < 4; d++) {
      const t = step(c, d);
      if (t < 0 || prev[t] !== -2) continue;
      if (t !== to && (kind[t] !== Kind.Empty || (patch && patch[t]))) continue;
      prev[t] = c;
      q.push(t);
    }
  }
  if (prev[to] === -2) return null;
  const path: number[] = [];
  for (let c = prev[to]; c !== from && c !== -1; c = prev[c]) path.push(c);
  path.reverse();
  return path;
}

/** Lay belts along `path`, each pointing at the next cell, ending pointed at `to`. */
function layBelts(layout: Layout, path: number[], to: number): void {
  for (let i = 0; i < path.length; i++) {
    const c = path[i];
    const next = i + 1 < path.length ? path[i + 1] : to;
    layout.kind[c] = Kind.Belt;
    for (let d = 0; d < 4; d++) if (step(c, d) === next) layout.dir[c] = d;
  }
}

function blob(rng: () => number, kind: Uint8Array, patch: Uint8Array, seam: number, size: number, taken: number[]): number[] {
  // Grow a contiguous blob from a free cell that is not too close to anything already placed.
  const candidates: number[] = [];
  for (let c = 0; c < N; c++) {
    if (kind[c] !== Kind.Empty || patch[c]) continue;
    if (cy(c) > H - 4) continue; // keep seams off the wharf's row band
    let ok = true;
    for (const o of taken) if (Math.abs(cx(c) - cx(o)) + Math.abs(cy(c) - cy(o)) < 4) ok = false;
    if (ok) candidates.push(c);
  }
  if (!candidates.length) return [];
  const start = pick(rng, candidates);
  const cells = [start];
  patch[start] = seam;
  while (cells.length < size) {
    const from = pick(rng, cells);
    const dirs = shuffled(rng, [0, 1, 2, 3]);
    let grew = false;
    for (const d of dirs) {
      const t = step(from, d);
      if (t < 0 || patch[t] || kind[t] !== Kind.Empty || cy(t) > H - 4) continue;
      patch[t] = seam;
      cells.push(t);
      grew = true;
      break;
    }
    if (!grew) break;
  }
  return cells;
}

/** Every promise the rest of the game relies on, re-derived from the finished board. */
export function validate(b: Board): string | null {
  const { kind, patch } = b.layout;
  let shale = 0;
  let loam = 0;
  // WHAT MATTERS IS A DIGGABLE CELL, NOT EVERY CELL. The first version of this check demanded that
  // every seam cell have a free orthogonal neighbour, which condemned the INTERIOR of every seam it
  // generated — a cell in the middle of a four-cell blob is surrounded by its own seam, and that is
  // completely fine, because you put the digger on the rim. It rejected 124 of 125 boards and the
  // generator spent nine re-rolls a seed papering over a bug in its own validator.
  for (let c = 0; c < N; c++) {
    if (!patch[c] || kind[c] !== Kind.Empty) continue;
    let free = 0;
    for (let d = 0; d < 4; d++) {
      const t = step(c, d);
      if (t >= 0 && kind[t] === Kind.Empty && !patch[t]) free++;
    }
    if (free < 1) continue;
    if (patch[c] === 1) shale++;
    else loam++;
  }
  if (shale < 2) return `only ${shale} diggable shale cells`;
  if (loam < 2) return `only ${loam} diggable loam cells`;
  // The starter line must actually be shipping.
  let diggers = 0;
  let smelters = 0;
  for (let c = 0; c < N; c++) {
    if (kind[c] === Kind.Digger) diggers++;
    if (kind[c] === Kind.Smelter) smelters++;
  }
  if (diggers < 1 || smelters < 1) return 'no starter line';

  // THE PROMISE IS SIMULATED, NOT INSPECTED. "There is a digger and there is a smelter" is a
  // structural claim about the pieces; "this board opens already earning" is a claim about the
  // whole machine, and only running it can settle that. Seed 36 passed every structural check and
  // shipped nothing for thirty seconds, because its smelter had a lane in but no lane out. Thirty
  // seconds of sim is well under a millisecond, so the honest check is also the cheap one.
  const probe = createState('steady', 0, b.layout, b.quirks);
  const opening = probe.scrip;
  run(probe, 30 * 20);
  if (probe.scrip <= opening) return 'the starter line ships nothing in its first thirty seconds';
  return null;
}

function build(seedNum: number, attempt: number): Board {
  const rng = makeRng(seedNum + attempt * 7919);
  const layout: Layout = { kind: new Uint8Array(N), dir: new Uint8Array(N), patch: new Uint8Array(N) };
  const quirks = quirksFor(rng);

  // The wharf: a fixed 2x2 on the bottom edge.
  const wx = 1 + Math.floor(rng() * (W - 3));
  const wharf: number[] = [];
  for (let dy = 0; dy < 2; dy++) {
    for (let dx = 0; dx < 2; dx++) {
      const c = idx(wx + dx, H - 2 + dy);
      layout.kind[c] = Kind.Wharf;
      wharf.push(c);
    }
  }

  // Seams.
  const taken: number[] = wharf.slice();
  const shaleSeams = has(quirks, 'scattershale') ? 5 : 3;
  const shaleSize = has(quirks, 'scattershale') ? 2 : 4;
  const loamSeams = has(quirks, 'thinloam') ? 1 : 2;
  for (let i = 0; i < shaleSeams; i++) {
    const cells = blob(rng, layout.kind, layout.patch, 1, shaleSize, taken);
    if (cells.length) taken.push(cells[0]);
  }
  for (let i = 0; i < loamSeams; i++) {
    const cells = blob(rng, layout.kind, layout.patch, 2, 4, taken);
    if (cells.length) taken.push(cells[0]);
  }

  // Boulders — never orthogonally adjacent to a seam or the wharf, so nothing gets walled in.
  const boulders = has(quirks, 'rubble') ? 22 : has(quirks, 'swept') ? 2 : 10;
  const free: number[] = [];
  for (let c = 0; c < N; c++) {
    if (layout.kind[c] !== Kind.Empty || layout.patch[c]) continue;
    let ok = true;
    for (let d = 0; d < 4; d++) {
      const t = step(c, d);
      if (t >= 0 && (layout.patch[t] || layout.kind[t] === Kind.Wharf)) ok = false;
    }
    if (ok) free.push(c);
  }
  for (const c of shuffled(rng, free).slice(0, boulders)) layout.kind[c] = Kind.Boulder;

  // ── the starter line: one digger, one smelter, and a lane to the wharf ────────────────────
  const shaleCells: number[] = [];
  for (let c = 0; c < N; c++) if (layout.patch[c] === 1 && layout.kind[c] === Kind.Empty) shaleCells.push(c);
  const dist = (a: number, b: number): number => Math.abs(cx(a) - cx(b)) + Math.abs(cy(a) - cy(b));
  shaleCells.sort((a, b) => dist(a, wharf[0]) - dist(b, wharf[0]) || a - b);

  // ROUTE TO THE WHARF FIRST, THEN PUT THE SMELTER ON THE PATH. The obvious construction — pick a
  // smelter site, route seam→smelter, then route smelter→wharf — fails about half the time, because
  // the first run of belts is itself an obstacle to the second, and the failure is invisible until
  // you simulate it. Cutting the smelter INTO a path that already reaches the wharf cannot fail:
  // the belts before it point into it and the belts after it point onward, which is exactly the
  // feed-and-ship relationship the machine needs. 96 seeds in 200 needed a re-roll before this;
  // afterwards the router is correct by construction rather than by retry.
  for (const dig of shaleCells) {
    const path = route(layout.kind, dig, wharf[0], layout.patch);
    // At least one belt must sit between the digger and the smelter: a digger only ever emits onto
    // a carrier or the wharf, never straight into a machine.
    if (!path || path.length < 2) continue;
    const at = Math.min(path.length - 1, Math.max(1, Math.floor(path.length / 2)));
    const sm = path[at];
    const before = path.slice(0, at);
    const after = path.slice(at + 1);
    layBelts(layout, before, sm);
    layBelts(layout, after, wharf[0]);
    layout.kind[sm] = Kind.Smelter;
    layout.dir[sm] = 0;
    layout.kind[dig] = Kind.Digger;
    return { layout, quirks, wharf, rerolls: attempt };
  }
  return { layout, quirks, wharf, rerolls: attempt };
}

/** Exposed for the generator diagnostics test only. */
export const __build = build;

export function generate(seedNum: number): Board {
  for (let attempt = 0; attempt < 24; attempt++) {
    const b = build(seedNum, attempt);
    if (!validate(b)) return b;
  }
  // Never reached in 20,000 offline seeds; if it ever is, a playable board beats a hang.
  return build(seedNum, 0);
}

export const kindAt = (b: Board, c: number): KindT => b.layout.kind[c] as KindT;
